/**
 * Admin Calculator Engine dashboard.
 *
 * Three tabs:
 *   • Catalog    — every registered calculator + analytics rollup
 *   • Leads      — calculator_leads inbox (filterable by slug)
 *   • Cost Tables — static cost tables side-by-side with Mongo overrides;
 *                   admin can upsert overrides inline.
 *
 * All data is rule-based + DB-backed. No AI involvement here.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, Loader2, Calculator, Inbox, Database,
    Wrench, Mail, ExternalLink, ImageIcon, Sparkles,
    Activity, CheckCircle2, AlertTriangle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import PublicLayout from "@/components/layout/PublicLayout";
import { categoryLabel } from "@/lib/tools/category-meta";
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
    adminListCalculators,
    adminListCalculatorLeads,
    adminGetCalculatorCostOverrides,
    adminUpsertCalculatorCostOverride,
    adminGeneratePinterestImages,
    adminCalculatorHealth,
} from "@/lib/api";


function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso.slice(0, 16).replace("T", " ");
    }
}

function fmtDollars(n) {
    if (n === null || n === undefined || isNaN(Number(n))) return "—";
    return `$${Math.round(Number(n)).toLocaleString()}`;
}

function MiniStat({ label, value, testid }) {
    return (
        <div className="bl-card p-4" data-testid={testid}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                {label}
            </div>
            <div className="mt-1.5 font-display font-semibold text-2xl text-bl tnum">
                {value ?? "—"}
            </div>
        </div>
    );
}


/**
 * 30-day pass/fail sparkline (top row) + per-day category heatmap (bottom
 * row). The top row gives the binary "broken today / clean today" pulse.
 * The bottom row reveals WHICH calculator broke and in WHICH category —
 * so operators can spot category-wide regressions (e.g. "every plumbing
 * calc started failing the day we shipped that cost-table refactor")
 * that the top row would compress into a single red cell.
 *
 * Why this matters: a shared cost-table change can silently break a whole
 * category of calculators while leaving the rest healthy. The single-row
 * view says "1 day red." The two-row view says "1 day red — all 4 plumbing
 * calcs at once" → instantly actionable.
 */
function HealthSparkline({ history }) {
    // Stable category palette. Categories present in `CATEGORY_META` use the
    // brand colors; unknown categories fall back to a neutral mid-gray so
    // they're still visible without polluting the legend.
    const CATEGORY_COLORS = {
        foundation:      "#8B5E3C",  // brown — earth/dirt
        water_intrusion: "#2C7B8E",  // teal — water
        roofing:         "#7A4B2E",  // umber — shingle
        hvac:            "#7D5BA6",  // violet — climate
        electrical:      "#D4A017",  // amber — voltage
        plumbing:        "#3F73C0",  // blue — pipes
        chimney:         "#A23B30",  // rust — fire
        negotiation:     "#0F5C63",  // brand teal
        costs:           "#A04E1A",  // burnt — dollars
        decision:        "#5A5E64",  // slate — judgment
        specialty:       "#6B7280",  // neutral
        exterior:        "#5A7A4C",  // sage — outside
    };
    const categoryColor = (cat) => CATEGORY_COLORS[cat] || "#9CA3AF";

    // Build a continuous 30-day window (most recent → oldest, right-to-left)
    // so missing days (e.g., backend restart skipped a sweep) render as gaps
    // rather than collapsing the strip.
    const cells = useMemo(() => {
        const byDate = new Map(history.map((h) => [h.date, h]));
        const today = new Date();
        const out = [];
        for (let i = 29; i >= 0; i--) {
            const d = new Date(today);
            d.setUTCDate(d.getUTCDate() - i);
            const iso = d.toISOString().slice(0, 10);
            const snap = byDate.get(iso);
            out.push({ date: iso, snap });
        }
        return out;
    }, [history]);

    const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
    const failingDays = cells.filter((c) => c.snap && !c.snap.all_healthy).length;
    const recordedDays = cells.filter((c) => c.snap).length;

    // Distinct categories that have failed at least once in the window —
    // drives the legend so we only show colors that actually appear.
    const failingCategories = useMemo(() => {
        const set = new Set();
        for (const c of cells) {
            for (const f of c.snap?.failures || []) {
                if (f.category) set.add(f.category);
            }
        }
        return Array.from(set).sort();
    }, [cells]);

    return (
        <div className="mt-4" data-testid="calc-health-sparkline">
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider text-bl-muted font-semibold">
                    Last 30 days
                </span>
                <span className="text-[11px] text-bl-muted font-mono-bl tnum">
                    {failingDays === 0
                        ? `${recordedDays}/30 clean`
                        : `${failingDays} bad / ${recordedDays} recorded`}
                </span>
            </div>

            {/* Row 1: binary pass/fail per day */}
            <div className="flex gap-0.5" data-testid="spark-row-status">
                {cells.map(({ date, snap }) => {
                    const isToday = date === todayIso;
                    let cls = "bg-bl-cream border border-bl/10";
                    let title = `${date} — no audit run`;
                    if (snap) {
                        if (snap.all_healthy) {
                            cls = "bg-green-500/70 border border-green-600/40";
                            title = `${date} — all ${snap.total_calculators} healthy`;
                        } else {
                            cls = "bg-red-500/80 border border-red-700/40";
                            title = `${date} — ${snap.failing} of ${snap.total_calculators} failing`;
                        }
                    }
                    return (
                        <div
                            key={date}
                            title={title}
                            data-testid={`spark-cell-${date}`}
                            className={[
                                "flex-1 h-5 rounded-sm transition-transform hover:scale-y-125 cursor-help",
                                cls,
                                isToday ? "ring-2 ring-bl-primary/70 ring-offset-1 ring-offset-bl-bg" : "",
                            ].join(" ")}
                        />
                    );
                })}
            </div>

            {/* Row 2: which calc failed (color-coded by category). Stacks
                multiple failures vertically inside each day's cell. */}
            <div className="flex gap-0.5 mt-0.5" data-testid="spark-row-heatmap">
                {cells.map(({ date, snap }) => {
                    const failures = snap?.failures || [];
                    if (failures.length === 0) {
                        return (
                            <div
                                key={date}
                                className="flex-1 h-3 rounded-sm bg-bl-cream/40"
                                title={`${date} — no failures`}
                            />
                        );
                    }
                    return (
                        <div
                            key={date}
                            className="flex-1 h-3 rounded-sm overflow-hidden flex flex-col"
                            title={`${date} — ${failures.map((f) => f.slug).join(", ")}`}
                            data-testid={`spark-heatmap-${date}`}
                        >
                            {failures.slice(0, 6).map((f, idx) => (
                                <div
                                    key={`${f.slug}-${idx}`}
                                    className="flex-1 min-h-0"
                                    style={{ backgroundColor: categoryColor(f.category) }}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>

            {/* Legend: status row first, then per-category colors that
                actually appeared in the window. Keeps the legend honest
                instead of advertising every possible category color. */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-[10px] text-bl-muted">
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-500/70" />
                    Healthy
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-500/80" />
                    Failing
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-bl-cream border border-bl/20" />
                    No data
                </span>
                {failingCategories.length > 0 && (
                    <>
                        <span className="text-bl-muted/40">·</span>
                        {failingCategories.map((cat) => (
                            <span key={cat} className="inline-flex items-center gap-1.5">
                                <span
                                    className="inline-block w-2.5 h-2.5 rounded-sm"
                                    style={{ backgroundColor: categoryColor(cat) }}
                                />
                                {categoryLabel(cat)}
                            </span>
                        ))}
                    </>
                )}
                <span className="inline-flex items-center gap-1.5 ml-auto">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-500/70 ring-2 ring-bl-primary/70 ring-offset-1 ring-offset-bl-bg" />
                    Today
                </span>
            </div>
        </div>
    );
}


/**
 * Calculator Health tile — runs the same audit the nightly background loop
 * runs (every calculator with severe inputs), and shows pass/fail at a
 * glance. Admins can re-run on demand via the refresh button to verify a
 * fix worked without waiting for the next nightly sweep.
 */
function HealthTile() {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [data, setData] = useState(null);
    // Bump this to force a re-fetch. Increment from the "Re-run" button.
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await adminCalculatorHealth();
                if (cancelled) return;
                setData(r);
            } catch {
                if (!cancelled) toast.error("Could not run calculator health audit.");
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setRefreshing(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [reloadKey]);

    const handleRefresh = () => {
        if (refreshing) return;
        setRefreshing(true);
        setReloadKey((k) => k + 1);
    };

    if (loading) {
        return (
            <div
                className="bl-card p-5 flex items-center gap-3 text-sm text-bl-muted"
                data-testid="calc-health-tile-loading"
            >
                <Loader2 className="h-4 w-4 animate-spin" />
                Running calculator audit…
            </div>
        );
    }

    if (!data) return null;
    const healthy = data.all_healthy;
    const total = data.total_calculators || 0;
    const failing = data.failing || 0;
    const failures = data.failures || [];

    return (
        <div
            className={[
                "bl-card p-5",
                healthy
                    ? "border-2 border-green-500/40 bg-green-50/40"
                    : "border-2 border-red-500/50 bg-red-50/40",
            ].join(" ")}
            data-testid="calc-health-tile"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                    <div
                        className={[
                            "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                            healthy
                                ? "bg-green-500/15 text-green-700"
                                : "bg-red-500/15 text-red-700",
                        ].join(" ")}
                    >
                        {healthy ? (
                            <CheckCircle2 className="h-5 w-5" />
                        ) : (
                            <AlertTriangle className="h-5 w-5" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Activity className="h-3.5 w-3.5 text-bl-muted" />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-semibold">
                                Calculator Health
                            </span>
                        </div>
                        <h3
                            className="mt-1 font-display font-semibold text-lg text-bl"
                            data-testid="calc-health-status"
                        >
                            {healthy
                                ? `All ${total} calculators healthy`
                                : `${failing} of ${total} calculators failing`}
                        </h3>
                        <p className="mt-1 text-xs text-bl-muted leading-relaxed">
                            {healthy
                                ? "Every cost-bearing calculator returns a non-zero range with severe inputs. Audit re-runs nightly."
                                : "One or more cost-bearing calculators returned $0 with severe inputs. Admin alert email has been queued."}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="bl-btn-ghost text-xs whitespace-nowrap shrink-0"
                    data-testid="calc-health-refresh"
                    title="Re-run audit"
                >
                    <RefreshCw
                        className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`}
                    />
                    Re-run
                </button>
            </div>

            {/* 30-day status sparkline — one cell per snapshot day so flaky
                calcs (pass most days, fail occasionally) become visible at
                a glance instead of being hidden by a single point-in-time
                check. */}
            {Array.isArray(data.history) && data.history.length > 0 && (
                <HealthSparkline history={data.history} />
            )}

            {!healthy && failures.length > 0 && (
                <div
                    className="mt-4 rounded-lg bg-white border border-red-500/30 overflow-hidden"
                    data-testid="calc-health-failures"
                >
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-red-500/20 bg-red-50/40">
                                <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-red-800 font-semibold">
                                    Slug
                                </th>
                                <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-red-800 font-semibold">
                                    Range
                                </th>
                                <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-red-800 font-semibold">
                                    Symptom
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {failures.map((f) => (
                                <tr
                                    key={f.slug}
                                    className="border-b border-bl/10 last:border-b-0"
                                    data-testid={`calc-health-failure-${f.slug}`}
                                >
                                    <td className="py-2 px-3 font-mono text-xs text-bl">
                                        <Link
                                            to={`/tools/${f.slug}`}
                                            className="text-bl-primary hover:underline"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {f.slug}
                                        </Link>
                                    </td>
                                    <td className="py-2 px-3 text-xs text-bl-muted tnum">
                                        {fmtDollars(f.cost_low)} – {fmtDollars(f.cost_high)}
                                    </td>
                                    <td className="py-2 px-3 text-xs text-red-700 font-mono">
                                        {f.error || "$0 with severe inputs"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ─── Catalog tab ────────────────────────────────────────────────────

function CatalogTab() {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [genBusy, setGenBusy] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await adminListCalculators();
                if (!cancelled) setItems(r.items || []);
            } catch (e) {
                if (!cancelled) toast.error("Could not load calculator catalog.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [reloadKey]);

    const onGenerateCovers = async (force) => {
        if (genBusy) return;
        setGenBusy(true);
        toast.info(
            force ? "Regenerating all Pinterest covers…" : "Generating missing Pinterest covers…",
            { description: "This may take 1-3 minutes." },
        );
        try {
            const r = await adminGeneratePinterestImages({ force });
            toast.success(
                `Pinterest covers · ${r.generated.length} generated · ${r.skipped.length} skipped` +
                (r.failed?.length ? ` · ${r.failed.length} failed` : ""),
            );
            setReloadKey((k) => k + 1);  // Trigger refetch
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed.");
        } finally {
            setGenBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-bl-muted py-12">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading catalog…
            </div>
        );
    }

    const totals = items.reduce(
        (acc, c) => ({
            calcs: acc.calcs + 1,
            published: acc.published + (c.published ? 1 : 0),
            leads: acc.leads + (c.analytics?.lead_count_total || 0),
            completes: acc.completes + (c.analytics?.completed_count_total || 0),
            covers: acc.covers + (c.pinterest?.image_url ? 1 : 0),
        }),
        { calcs: 0, published: 0, leads: 0, completes: 0, covers: 0 },
    );

    return (
        <div className="space-y-6" data-testid="calc-admin-catalog">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <MiniStat label="Calculators" value={totals.calcs} testid="cnt-calcs" />
                <MiniStat label="Published" value={totals.published} testid="cnt-published" />
                <MiniStat label="Completions" value={totals.completes} testid="cnt-completes" />
                <MiniStat label="Leads captured" value={totals.leads} testid="cnt-leads" />
                <MiniStat label="Pinterest covers" value={`${totals.covers}/${totals.calcs}`} testid="cnt-covers" />
            </div>

            <HealthTile />

            <div className="bl-card p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold text-bl flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        Pinterest cover artwork
                    </div>
                    <div className="text-xs text-bl-muted mt-1 max-w-2xl">
                        One-shot Nano-Banana batch. Generates a claymation-style
                        cover for every calculator from its <code>pinterest_image_prompt</code>.
                        Idempotent — already-generated covers are skipped unless you force regen.
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => onGenerateCovers(false)}
                        disabled={genBusy}
                        className="bl-btn-primary text-sm flex items-center gap-1.5"
                        data-testid="gen-pinterest-covers-missing"
                    >
                        {genBusy ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                        )}
                        Generate missing
                    </button>
                    <button
                        type="button"
                        onClick={() => onGenerateCovers(true)}
                        disabled={genBusy}
                        className="bl-btn-ghost text-sm"
                        data-testid="gen-pinterest-covers-force"
                    >
                        Regenerate all
                    </button>
                </div>
            </div>

            <div className="bl-card overflow-x-auto">
                <table className="w-full text-sm" data-testid="calc-admin-catalog-table">
                    <thead className="bg-bl-bg text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        <tr>
                            <th className="text-left px-4 py-3">Cover</th>
                            <th className="text-left px-4 py-3">Calculator</th>
                            <th className="text-left px-4 py-3">Category</th>
                            <th className="text-right px-4 py-3">Inputs</th>
                            <th className="text-right px-4 py-3">Completions</th>
                            <th className="text-right px-4 py-3">Leads</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-left px-4 py-3">View</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-bl/15">
                        {items.map((c) => (
                            <tr key={c.slug} data-testid={`calc-row-${c.slug}`}>
                                <td className="px-4 py-3 w-16">
                                    {c.pinterest?.image_url ? (
                                        <img
                                            src={c.pinterest.image_url}
                                            alt=""
                                            className="h-10 w-16 object-cover rounded border border-bl/20"
                                            data-testid={`calc-cover-${c.slug}`}
                                        />
                                    ) : (
                                        <div
                                            className="h-10 w-16 rounded border border-dashed border-bl/30 bg-bl-bg/50 flex items-center justify-center"
                                            data-testid={`calc-cover-missing-${c.slug}`}
                                        >
                                            <ImageIcon className="h-3.5 w-3.5 text-bl-muted" />
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-bl">{c.title}</div>
                                    <div className="text-[11px] text-bl-muted truncate max-w-md">
                                        {c.short_description}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-bl-muted">
                                    {(c.category || "").replace(/_/g, " ")}
                                </td>
                                <td className="px-4 py-3 text-right tnum">
                                    {c.input_schema?.length || 0}
                                </td>
                                <td className="px-4 py-3 text-right tnum">
                                    {c.analytics?.completed_count_total ?? 0}
                                </td>
                                <td className="px-4 py-3 text-right tnum">
                                    {c.analytics?.lead_count_total ?? 0}
                                </td>
                                <td className="px-4 py-3">
                                    {c.published ? (
                                        <span className="text-bl-primary text-xs font-semibold">
                                            Published
                                        </span>
                                    ) : (
                                        <span className="text-bl-muted text-xs">Draft</span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <Link
                                        to={`/tools/${c.slug}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-bl-teal hover:underline text-xs flex items-center gap-1"
                                        data-testid={`calc-row-link-${c.slug}`}
                                    >
                                        Open <ExternalLink className="h-3 w-3" />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={8} className="px-4 py-12 text-center text-bl-muted">
                                    No calculators registered.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Leads tab ──────────────────────────────────────────────────────

function LeadsTab({ catalogItems }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ items: [], total: 0 });
    const [slug, setSlug] = useState("");
    const [skip, setSkip] = useState(0);
    const limit = 100;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            setLoading(true);
            try {
                const r = await adminListCalculatorLeads({
                    limit, skip, slug: slug || undefined,
                });
                if (!cancelled) setData(r);
            } catch (e) {
                if (!cancelled) toast.error("Could not load calculator leads.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug, skip]);

    const slugOptions = useMemo(
        () => [{ slug: "", title: "All calculators" }].concat(
            (catalogItems || []).map((c) => ({ slug: c.slug, title: c.title })),
        ),
        [catalogItems],
    );

    return (
        <div className="space-y-4" data-testid="calc-admin-leads">
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-bl-muted block mb-1">
                        Filter by calculator
                    </label>
                    <select
                        value={slug}
                        onChange={(e) => { setSlug(e.target.value); setSkip(0); }}
                        className="bl-input min-w-[260px]"
                        data-testid="calc-leads-slug-filter"
                    >
                        {slugOptions.map((o) => (
                            <option key={o.slug || "all"} value={o.slug}>
                                {o.title}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="text-xs text-bl-muted ml-auto" data-testid="calc-leads-total">
                    {loading ? "Loading…" : `${data.total} total · showing ${data.items.length}`}
                </div>
            </div>

            <div className="bl-card overflow-x-auto">
                <table className="w-full text-sm" data-testid="calc-admin-leads-table">
                    <thead className="bg-bl-bg text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        <tr>
                            <th className="text-left px-4 py-3">Captured</th>
                            <th className="text-left px-4 py-3">Email</th>
                            <th className="text-left px-4 py-3">Calculator</th>
                            <th className="text-left px-4 py-3">Severity</th>
                            <th className="text-left px-4 py-3">Negotiation</th>
                            <th className="text-right px-4 py-3">Cost range</th>
                            <th className="text-left px-4 py-3">Source</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-bl/15">
                        {data.items.map((row, idx) => (
                            <tr key={`${row.email}-${row.ts}-${idx}`} data-testid="calc-leads-row">
                                <td className="px-4 py-3 text-bl-muted text-xs tnum whitespace-nowrap">
                                    {fmtDateTime(row.ts)}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1.5">
                                        <Mail className="h-3 w-3 text-bl-muted" />
                                        <a
                                            href={`mailto:${row.email}`}
                                            className="text-bl-teal hover:underline"
                                            data-testid="calc-leads-email"
                                        >
                                            {row.email}
                                        </a>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-xs text-bl-muted">
                                    <Link
                                        to={`/tools/${row.calculator_slug}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="hover:text-bl"
                                    >
                                        {row.calculator_slug}
                                    </Link>
                                </td>
                                <td className="px-4 py-3 text-xs capitalize">
                                    {row.severity_level || "—"}
                                </td>
                                <td className="px-4 py-3 text-xs capitalize">
                                    {(row.negotiation_level || "—").replace(/_/g, " ")}
                                </td>
                                <td className="px-4 py-3 text-right tnum text-xs">
                                    {row.cost_low !== null && row.cost_low !== undefined
                                        ? `${fmtDollars(row.cost_low)} – ${fmtDollars(row.cost_high)}`
                                        : "—"}
                                </td>
                                <td className="px-4 py-3 text-xs text-bl-muted">
                                    {row.source || "—"}
                                </td>
                            </tr>
                        ))}
                        {!loading && data.items.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-4 py-12 text-center text-bl-muted">
                                    No leads yet for this filter.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-between items-center text-xs text-bl-muted">
                <button
                    type="button"
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    disabled={skip === 0 || loading}
                    className="bl-btn-ghost text-xs"
                    data-testid="calc-leads-prev"
                >
                    ← Previous
                </button>
                <span>Page {Math.floor(skip / limit) + 1}</span>
                <button
                    type="button"
                    onClick={() => setSkip(skip + limit)}
                    disabled={loading || data.items.length < limit}
                    className="bl-btn-ghost text-xs"
                    data-testid="calc-leads-next"
                >
                    Next →
                </button>
            </div>
        </div>
    );
}

// ─── Cost-Table overrides tab ───────────────────────────────────────

function CostTablesTab() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ static_tables: {}, overrides: [] });
    const [edit, setEdit] = useState(null); // { table, key, low, high }
    const [busy, setBusy] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            setLoading(true);
            try {
                const r = await adminGetCalculatorCostOverrides();
                if (!cancelled) setData(r);
            } catch (e) {
                if (!cancelled) toast.error("Could not load cost tables.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [reloadKey]);

    const overrideMap = useMemo(() => {
        const m = {};
        (data.overrides || []).forEach((o) => {
            m[`${o.table}.${o.key}`] = o;
        });
        return m;
    }, [data.overrides]);

    const onSave = async () => {
        if (!edit) return;
        const low = Number(edit.low);
        const high = Number(edit.high);
        if (isNaN(low) || isNaN(high)) {
            toast.error("Enter numeric values.");
            return;
        }
        if (low > high) {
            toast.error("Low must be ≤ high.");
            return;
        }
        setBusy(true);
        try {
            await adminUpsertCalculatorCostOverride({
                table: edit.table, key: edit.key, low, high,
            });
            toast.success(`Override saved · ${edit.table}.${edit.key}`);
            setEdit(null);
            setReloadKey((k) => k + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-bl-muted py-12">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading cost tables…
            </div>
        );
    }

    const tableKeys = Object.keys(data.static_tables || {}).sort();

    return (
        <div className="space-y-6" data-testid="calc-admin-cost-tables">
            <p className="text-xs text-bl-muted max-w-2xl">
                Static cost ranges baked into the engine. Overrides are stored
                in <code className="text-bl">calculator_cost_overrides</code> and
                merged at calculation time. Effective range is{" "}
                <span className="font-semibold">override → static</span>.
            </p>

            {tableKeys.map((table) => {
                const entries = data.static_tables[table] || {};
                const keys = Object.keys(entries).sort();
                return (
                    <details
                        key={table}
                        className="bl-card overflow-hidden"
                        data-testid={`cost-table-${table}`}
                        open={table === tableKeys[0]}
                    >
                        <summary className="px-4 py-3 cursor-pointer flex items-center justify-between bg-bl-bg">
                            <div>
                                <span className="font-semibold text-bl uppercase tracking-wide text-sm">
                                    {table}
                                </span>
                                <span className="ml-3 text-xs text-bl-muted">
                                    {keys.length} item(s)
                                </span>
                            </div>
                            <span className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                                {keys.filter((k) => overrideMap[`${table}.${k}`]).length} overridden
                            </span>
                        </summary>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                                    <tr>
                                        <th className="text-left px-4 py-2">Key</th>
                                        <th className="text-right px-4 py-2">Static low</th>
                                        <th className="text-right px-4 py-2">Static high</th>
                                        <th className="text-right px-4 py-2">Override low</th>
                                        <th className="text-right px-4 py-2">Override high</th>
                                        <th className="text-right px-4 py-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-bl/15">
                                    {keys.map((key) => {
                                        const stat = entries[key];
                                        const ov = overrideMap[`${table}.${key}`];
                                        const editing = edit?.table === table && edit?.key === key;
                                        return (
                                            <tr key={key} data-testid={`cost-row-${table}-${key}`}>
                                                <td className="px-4 py-2 text-xs font-mono text-bl">
                                                    {key}
                                                </td>
                                                <td className="px-4 py-2 text-right tnum text-xs">
                                                    {fmtDollars(stat.low)}
                                                </td>
                                                <td className="px-4 py-2 text-right tnum text-xs">
                                                    {fmtDollars(stat.high)}
                                                </td>
                                                <td className="px-4 py-2 text-right tnum text-xs">
                                                    {editing ? (
                                                        <input
                                                            type="number"
                                                            className="bl-input w-24 text-right"
                                                            value={edit.low}
                                                            onChange={(e) =>
                                                                setEdit({ ...edit, low: e.target.value })}
                                                            data-testid="cost-edit-low"
                                                        />
                                                    ) : (
                                                        <span className={ov ? "text-bl-primary font-semibold" : "text-bl-muted"}>
                                                            {ov ? fmtDollars(ov.low) : "—"}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-right tnum text-xs">
                                                    {editing ? (
                                                        <input
                                                            type="number"
                                                            className="bl-input w-24 text-right"
                                                            value={edit.high}
                                                            onChange={(e) =>
                                                                setEdit({ ...edit, high: e.target.value })}
                                                            data-testid="cost-edit-high"
                                                        />
                                                    ) : (
                                                        <span className={ov ? "text-bl-primary font-semibold" : "text-bl-muted"}>
                                                            {ov ? fmtDollars(ov.high) : "—"}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    {editing ? (
                                                        <div className="flex justify-end gap-1.5">
                                                            <button
                                                                type="button"
                                                                onClick={onSave}
                                                                disabled={busy}
                                                                className="bl-btn-primary text-xs py-1 px-2"
                                                                data-testid="cost-edit-save"
                                                            >
                                                                {busy ? "Saving…" : "Save"}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setEdit(null)}
                                                                className="bl-btn-ghost text-xs py-1 px-2"
                                                                data-testid="cost-edit-cancel"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => setEdit({
                                                                table, key,
                                                                low: ov?.low ?? stat.low,
                                                                high: ov?.high ?? stat.high,
                                                            })}
                                                            className="bl-btn-ghost text-xs py-1 px-2"
                                                            data-testid={`cost-edit-${table}-${key}`}
                                                        >
                                                            {ov ? "Edit" : "Override"}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </details>
                );
            })}
        </div>
    );
}

// ─── Page shell ─────────────────────────────────────────────────────

export default function AdminCalculators() {
    const [catalog, setCatalog] = useState([]);

    useEffect(() => {
        // Pre-fetch the catalog so the leads filter can label slugs.
        (async () => {
            try {
                const r = await adminListCalculators();
                setCatalog(r.items || []);
            } catch (e) {
                // soft fail; LeadsTab will still render
            }
        })();
    }, []);

    return (
        <PublicLayout>
            <div
                className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-6"
                data-testid="admin-calculators"
            >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="bl-eyebrow">ADMIN</div>
                        <h1 className="font-display font-semibold text-2xl text-bl tracking-tight mt-1">
                            Calculator Engine
                        </h1>
                        <p className="text-sm text-bl-muted mt-1 max-w-2xl">
                            Read-only admin overview of every rule-based
                            calculator, captured leads, and the cost tables
                            powering each result.
                        </p>
                    </div>
                    <Link
                        to="/admin"
                        className="text-sm text-bl-muted hover:text-bl flex items-center gap-1.5"
                        data-testid="admin-calculators-back"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Back to admin
                    </Link>
                </div>

                <Tabs defaultValue="catalog">
                    <TabsList className="bg-bl-surface border border-bl">
                        <TabsTrigger value="catalog" data-testid="calc-admin-tab-catalog">
                            <Calculator className="h-3.5 w-3.5 mr-1.5" /> Catalog
                        </TabsTrigger>
                        <TabsTrigger value="leads" data-testid="calc-admin-tab-leads">
                            <Inbox className="h-3.5 w-3.5 mr-1.5" /> Leads
                        </TabsTrigger>
                        <TabsTrigger value="cost" data-testid="calc-admin-tab-cost">
                            <Database className="h-3.5 w-3.5 mr-1.5" /> Cost Tables
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="catalog" className="mt-5">
                        <CatalogTab />
                    </TabsContent>
                    <TabsContent value="leads" className="mt-5">
                        <LeadsTab catalogItems={catalog} />
                    </TabsContent>
                    <TabsContent value="cost" className="mt-5">
                        <CostTablesTab />
                    </TabsContent>
                </Tabs>
            </div>
        </PublicLayout>
    );
}
